import { GlobalStateController } from "bai-react-global-state";
import _ from "lodash";
import { v4 as uuid } from "uuid";

import getLogger, { LogGroup } from "js/core/logger";
import { DEFAULT_AI_IMAGEMODEL, DEFAULT_AI_IMAGEQUALITY } from "../../../../common/aiConstants";
import { AuthoringBlockType, TextStyleType } from "../../../../common/constants";
import { MetricName } from "../../../../common/interfaces/index";
import { findElementsWithImages } from "../../../../common/utils/findElementsWithImages";
import getObjectHash from "../../../../common/utils/getObjectHash";
import resolveImageSource from "../../../../common/utils/resolveImageSource";
import WorkspaceController from "../../../controllers/WorkspaceController";
import { applySlideImage, resetSlideImage } from "../../../core/models/slide";
import AiGenerationService from "../../../core/services/aiGeneration";
import { getTextFromBlocks } from "../../../core/utilities/htmlTextHelpers";
import { ShowWarningDialog } from "../../../react/components/Dialogs/BaseDialog";

const logger = getLogger(LogGroup.AI);

const initialState = {
    currentSlide: null,
    generatedIdeas: [],
    isGenerating: false,
    slideIdeas: [],
    slideCategories: [],
    slideImage: null,
    files: []
};

const SEPARATOR = "&nbsp;&nbsp;&nbsp;";

function getSourcesHtml(sources, html = "") {
    let titleCounts = {};
    let dedupedSources = _.cloneDeep(sources);
    dedupedSources = _.sortBy(dedupedSources, "title");
    dedupedSources.forEach(source => {
        titleCounts[source.title] = (titleCounts[source.title] || 0) + 1;
        if (titleCounts[source.title] > 1) {
            source.title = `${source.title} (${titleCounts[source.title]})`;
        }
    });

    let htmlChunks = [];
    for (let source of dedupedSources) {
        htmlChunks.push(`<a href="${source.url}" target="_blank" rel="noopener noreferrer">${source.title}</a>`);
    }

    return [html, ...htmlChunks].filter(Boolean).join(SEPARATOR);
}

export class SlideAiController extends GlobalStateController {
    get currentSlide() {
        return this._state.currentSlide;
    }

    getCategoryController() {
        return import(/* webpackMode: "eager" */ "common/SlideCategories/CategoryController").then(module => module.CategoryController);
    }

    async setCurrentSlide(slide, canvasController) {
        if (this._state.currentSlide) {
            this._state.currentSlide.off("change:metadata", this.onMetadataUpdated);
        }

        if (!slide) {
            await this.reset();
            return;
        }

        let metadata = slide.getMetadata();

        this.generatingImagesFor = null;

        await this._updateState({
            ...initialState,
            currentSlide: slide,
            canvasController,
            generatedIdeas: metadata.generatedIdeas || [],
            slideImage: metadata.generatedModel?.slideImage || null,
            slideCategories: [],
            isGenerating: false,
            generateError: metadata.generateError,
        });

        await this.resetFiles();

        slide.on("change:metadata", this.onMetadataUpdated);

        this.buildSlideModels();
    }

    async reset() {
        await this._updateState(() => ({
            ...initialState,
        }));
    }

    async resetFiles() {
        await this._updateState({
            files: this.getFilesFromSlideMetadata()
        });
    }

    getFilesFromSlideMetadata() {
        let { currentSlide } = this._state;
        let metadata = currentSlide.getMetadata();
        return metadata.files ? metadata.files.map(f => f.hasBeenUsedFor === "slide" ? ({ ...f, hasBeenUsed: false }) : f) : [];
    }

    setIsGenerating(isGenerating) {
        this._updateState({ isGenerating });
    }

    onMetadataUpdated = async (slide, changes) => {
        if (this._state.currentSlide !== slide) {
            return;
        }

        if (changes.generatedIdeas) {
            const metadata = slide.getMetadata();

            this._updateState({
                generatedIdeas: metadata.generatedIdeas || [],
                slideImage: metadata.generatedModel?.slideImage || null
            });

            this.buildSlideModels();
        } else if (changes.generatedModel?.slideImage) {
            this.buildSlideModels();
        }
    }

    buildPromptFromSlide(canvas) {
        let prompt = "";

        try {
            let header = canvas.getHeaderElement();
            if (header) {
                prompt += header.buildSlidePrompt() + "\n\n";
            }

            let primary = canvas.getPrimaryElement();
            if (primary) {
                prompt += primary.buildSlidePrompt() + "\n";
            }

            let sideBar = canvas.getSideBarElement();
            if (sideBar) {
                prompt += sideBar.buildSlidePrompt() + "\n";
            }

            let bottomTray = canvas.getTextTrayElement();
            if (bottomTray) {
                const trayText = getTextFromBlocks(bottomTray.model?.elementTextBlock).trim();
                if (trayText) {
                    prompt += "## Conclusion\n\n" + trayText + "\n";
                }
            }

            if (canvas.slide.getMetadata()?.generatedModel?.slideImage?.prompt) {
                prompt += "\nVisual: " + canvas.slide.getMetadata().generatedModel.slideImage.prompt + "\n";
            }
        } catch (err) {
            logger.error(err, "[SlideAiController] Error building prompt from slide");
        }

        return prompt;
    }

    async _waitForFilesToProcess() {
        let getFiles = () => this._state.files.filter(f => !f.hasBeenUsed);

        let processingFiles = getFiles().filter(f => f.isProcessing);
        while (processingFiles.length > 0) {
            await new Promise(resolve => setTimeout(resolve, 100));
            processingFiles = getFiles().filter(f => f.isProcessing);
        }

        return getFiles();
    }

    async regenerateIdeas(slidePrompt, {
        slideType,
        language,
        preserveExactText = false,
        usePresentationAsContext = true,
        webSearch = false,
        citeSources = false,
        imageSource = "ai",
        regenerateSlideImage = true,
        imageStyle = null,
        imageModel = DEFAULT_AI_IMAGEMODEL,
        imageQuality = DEFAULT_AI_IMAGEQUALITY
    } = {}) {
        imageModel = DEFAULT_AI_IMAGEMODEL;
        imageQuality = DEFAULT_AI_IMAGEQUALITY;

        const { currentSlide } = this._state;
        const startedAt = Date.now();
        const templateId = currentSlide?.get?.("templateId") ?? null;
        const imageCount = findElementsWithImages(currentSlide?.attributes ?? {}).length;
        let generationFailed = true;

        this._updateState({ isGenerating: true });

        try {
            const files = await this._waitForFilesToProcess();
            files.forEach(file => {
                file.hasBeenUsed = true;
                file.hasBeenUsedFor = "slide";
            });

            const genProps = {
                slidePrompt,
                slideType,
                language,
                preserveExactText,
                usePresentationAsContext,
                files,
                webSearch,
                citeSources
            };

            imageSource = resolveImageSource(imageSource, WorkspaceController.actionPermissions);

            if (!regenerateSlideImage && imageSource !== "none") {
                if (
                    // No slide image defined, have to generate anyway
                    !currentSlide.getMetadata().generatedModel?.slideImage ||
                    // Image source has changed, have to regenerate
                    currentSlide.getMetadata().imageSource !== imageSource

                ) {
                    regenerateSlideImage = true;
                }
            }

            imageStyle = imageStyle || currentSlide.getMetadata().generatedModel?.slideImage?.imageStyle || currentSlide.getMetadata().imageStyle || "";

            const slideImagePromise = (async () => {
                if (!regenerateSlideImage) {
                    return { metadata: {} };
                }

                let { slideImagePrompt } = await AiGenerationService.generateSlideImagePrompt(
                    slidePrompt,
                    usePresentationAsContext ? currentSlide.presentation.get("metadata")?.topic : null
                );

                let { slide } = await AiGenerationService.regenerateSlideImage({
                    slide: currentSlide.attributes,
                    imagePrompt: slideImagePrompt,
                    imageStyle,
                    imageSource,
                    imageModel,
                    imageQuality,
                    replaceImages: false
                });

                return slide;
            })();

            let response;
            try {
                response = await AiGenerationService.generateSlideData({
                    slideId: currentSlide.id,
                    presentationId: currentSlide.presentation.id,
                    slideIndex: currentSlide.getIndex(),
                    presentationTopic: currentSlide.presentation.get("metadata")?.topic || "",
                    ...genProps
                });
            } catch (err) {
                logger.error(err, "[SlideAiController] Error generating slide data");
            }

            if (!response || !response.slides || response.slides.length == 0) {
                slideImagePromise.catch(err => logger.error(err, "[SlideAiController] Error generating slide image after slide data failed"));
                ShowWarningDialog({ title: "Slide AI", message: "Sorry, an error occurred while generating slide ideas. This may be a temporary issue so please try again." });

                this._updateState({ isGenerating: false });
                return;
            }

            if (this._state.currentSlide !== currentSlide) {
                slideImagePromise.catch(err => logger.error(err, "[SlideAiController] Error generating slide image after current slide changed"));
                return;
            }

            let generatedIdeas = response.slides.map(slide => slide.slideData);

            let savedFiles = [];
            if (files.length > 0) {
                ({ files: savedFiles } = await AiGenerationService.saveContextDocuments(files));
            }

            currentSlide.update({
                hasBeenViewed: true,
                metadata: {
                    ...currentSlide.getMetadata(),
                    ...genProps,
                    sources: response.sources ?? currentSlide.getMetadata().sources ?? [],
                    imageSource,
                    imageStyle,
                    imageModel,
                    imageQuality,
                    generatedIdeas,
                    files: savedFiles,
                    presentationAsContext: usePresentationAsContext,
                    gotUserFeedback: false,
                }
            }, { silent: true, saveUndo: false });

            if (regenerateSlideImage) {
                currentSlide.update(resetSlideImage(currentSlide.attributes, true), { silent: true, saveUndo: false });
            }

            await this._updateState({
                generatedIdeas,
                slideImage: currentSlide.getMetadata().generatedModel?.slideImage,
            });

            this.generatingImagesFor = null;

            await this.buildSlideModels();

            this._updateState({ isGenerating: false });

            if (!regenerateSlideImage) {
                generationFailed = false;
                return;
            }

            let slideImageUpdates = { metadata: {} };
            try {
                slideImageUpdates = await slideImagePromise;
            } catch (err) {
                logger.error(err, "[SlideAiController] Error generating slide image");
            }

            if (this._state.currentSlide !== currentSlide) {
                return;
            }

            if (slideImageUpdates.metadata?.generatedModel?.slideImage?.assetId) {
                currentSlide.update(applySlideImage(currentSlide.attributes, slideImageUpdates.metadata.generatedModel.slideImage), { silent: false, saveUndo: true });

                await this._updateState({
                    slideImage: currentSlide.getMetadata().generatedModel?.slideImage,
                });

                await this.buildSlideModels();
            }

            generationFailed = false;
        } catch (err) {
            this._updateState({ isGenerating: false });
            throw err;
        } finally {
            logger.metric(MetricName.AI_GENERATE_SLIDE_TIME, {
                generationTimeMs: Date.now() - startedAt,
                templateId,
                withContext: usePresentationAsContext,
                imageCount,
                generationFailed
            });
        }
    }

    async buildSlideModels() {
        const { currentSlide, generatedIdeas, slideImage } = this._state;

        if (!currentSlide) {
            await this._updateState({ slideCategories: [] });
            return;
        }

        let metadata = currentSlide.getMetadata();
        let imageSource = resolveImageSource(metadata?.imageSource ?? "ai", WorkspaceController.actionPermissions);
        let imageStyle = slideImage?.imageStyle ?? metadata?.imageStyle;
        let imageModel = slideImage?.imageModel ?? metadata?.imageModel;
        let imageQuality = slideImage?.imageQuality ?? metadata?.imageQuality;

        let slideCategories = [];
        for (let idea of generatedIdeas) {
            let slideModel = idea.slideModel;

            slideModel.slideImage = slideImage;

            // build slide models for this category
            let CategoryController = await this.getCategoryController();
            let category = CategoryController.getCategory(idea.slideCategory);
            if (category.validateGeneratedModel(slideModel) === false) {
                logger.warn(`[SlideAiController] Generated model for category ${idea.slideCategory} failed validation`);
                continue;
            }
            let models = category.getSlideModels(slideModel, { citeSources: metadata.citeSources }).filter(model => model != null);

            // copy over metadata from the current slide
            models = models.map(model => ({
                ...model,
                metadata: {
                    ...currentSlide.getMetadata(),
                    ...model.metadata,
                    generatedIdeas,
                    aiGenerated: true,
                    slideCategory: idea.slideCategory,
                    slideReasoning: idea.slideReasoning,
                    slideConfidence: idea.slideConfidence,
                    needsLayoutProcessing: true
                }
            }));

            models.forEach(model => {
                let elementsWithImages = findElementsWithImages(model);
                for (let element of elementsWithImages) {
                    if (element.__generateImage) {
                        element.__generateImage.imageStyle = imageStyle;
                        element.__generateImage.imageSource = slideImage?.imageSource ?? imageSource;
                        element.__generateImage.imageModel = imageModel;
                        element.__generateImage.imageQuality = imageQuality;

                        if (imageSource === "none") {
                            element.content_value = null;
                            element.__generateImage.isGenerating = false;
                        } else if (!element.content_value) {
                            element.__generateImage.isGenerating = true;
                        }
                    }
                }
            });

            slideCategories.push({
                slideCategory: category.name,
                slideReasoning: idea.slideReasoning,
                slideConfidence: idea.slideConfidence,
                models
            });
        }

        if (imageSource !== "none") {
            let ideasHash = getObjectHash(generatedIdeas);
            let shouldGenerateImages = this.generatingImagesFor !== ideasHash;
            if (shouldGenerateImages) {
                this.generatingImagesFor = ideasHash;
            }

            slideCategories.forEach((category, categoryIdx) => {
                category.models.forEach((model, idx) => {
                    let elementsWithImages = findElementsWithImages(model);
                    if (shouldGenerateImages && elementsWithImages.some(element => element.__generateImage.isGenerating && !element.__generateImage.isSlideImage)) {
                        AiGenerationService.generateImagesForSlide({ slide: { ...model, presentationId: currentSlide.get("presentationId") } })
                            .then(({ slide }) => {
                                if (this._state.currentSlide !== currentSlide) {
                                    return;
                                }

                                let generatedModel = _.omit(slide, "presentationId");
                                this._updateState(state => ({
                                    ...state,
                                    slideCategories: state.slideCategories.map((currentCategory, currentCategoryIdx) => {
                                        if (currentCategoryIdx !== categoryIdx) {
                                            return currentCategory;
                                        }

                                        return {
                                            ...currentCategory,
                                            models: currentCategory.models.map((currentModel, currentModelIdx) => {
                                                if (currentModelIdx !== idx) {
                                                    return currentModel;
                                                }

                                                return {
                                                    ...generatedModel,
                                                    slideImage: currentModel.slideImage ?? generatedModel.slideImage,
                                                    metadata: {
                                                        ...currentModel.metadata,
                                                        ...generatedModel.metadata,
                                                    }
                                                };
                                            })
                                        };
                                    })
                                }));
                            });
                    }
                });
            });
        }

        await this._updateState({ slideCategories });
    }

    async parseDocument(file) {
        while (this._state.files.some(f => f.name === file.name)) {
            file = new File([file], `_${file.name}`, { type: file.type });
        }

        await this._updateState(state => ({
            ...state,
            files: [...state.files, { name: file.name, type: file.type, isProcessing: true }]
        }));

        try {
            let parsedFile = await AiGenerationService.parseDocument(file, this._state.files);

            await this._updateState(state => ({
                ...state,
                files: [...state.files.filter(f => f.name !== parsedFile.name), parsedFile]
            }));
        } catch (err) {
            await this._updateState(state => ({
                ...state,
                files: state.files.filter(f => f.name !== file.name)
            }));

            throw err;
        }
    }

    async removeFile(name) {
        await this._updateState(state => ({
            ...state,
            files: state.files.filter(f => f.name !== name)
        }));
    }

    addSourcesToSlide(sources) {
        const { canvasController } = this._state;

        const canvas = canvasController.canvas;
        const model = canvas.model;

        if (!model.elements.elementAttribution) {
            model.elements.elementAttribution = {};
        }
        if (!model.elements.elementAttribution.elementAttribution) {
            model.elements.elementAttribution.elementAttribution = {};
        }
        if (!model.elements.elementAttribution.elementAttribution.blocks) {
            model.elements.elementAttribution.elementAttribution.blocks = [];
        }
        const textBlocks = model.elements.elementAttribution.elementAttribution.blocks.filter(block => block.type === AuthoringBlockType.TEXT);
        if (textBlocks.length === 0) {
            model.elements.elementAttribution.elementAttribution.blocks.push({
                id: uuid(),
                type: AuthoringBlockType.TEXT,
                html: "",
                textStyle: TextStyleType.BODY
            });
        }

        const block = model.elements.elementAttribution.elementAttribution.blocks.find(block => block.type === AuthoringBlockType.TEXT);
        let html = block.html;

        if (!model.layout) {
            model.layout = {};
        }
        if (!model.layout.showElementAttribution) {
            model.layout.showElementAttribution = true;
            // Treat as empty
            html = "";
        }

        block.html = getSourcesHtml(sources, html);

        return canvas.updateCanvasModel();
    }
}

const slideAiController = new SlideAiController(initialState);
export default slideAiController;

// DEV-START
if (import.meta.webpackHot) {
    const reloadIdeas = async () => {
        if (window.isReloadingIdeas) {
            // eslint-disable-next-line no-console
            console.log("[HMR] Already reloading ideas");
            return;
        }

        if (!slideAiController.currentSlide) {
            return;
        }

        // eslint-disable-next-line no-console
        console.log("[HMR] Reloading ideas");

        window.isReloadingIdeas = true;
        try {
            await slideAiController.buildSlideModels();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[HMR] Error reloading ideas", err);
        } finally {
            window.isReloadingIdeas = false;
        }
    };

    let isReady = false;
    const onWebpackReload = status => {
        if (status === "ready") {
            isReady = true;
            return;
        }

        if (status === "idle" && isReady) {
            return reloadIdeas();
        }
    };

    import.meta.webpackHot.addStatusHandler(onWebpackReload);

    import.meta.webpackHot.accept(["common/SlideCategories/CategoryController"], () => {
        return reloadIdeas();
    });
}
// DEV-END
